import { Component, inject, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { TranslateModule } from '@ngx-translate/core'
import { AddCallData, CallsService, MinutesStyling } from '../calls.service'
import { combineLatest, filter, map, take, tap } from 'rxjs'
import { AsyncPipe } from '@angular/common'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToastService } from '../toast.service'
import { ActivatedRoute, Router } from '@angular/router'
import { HttpClient } from '@angular/common/http'
import { MatIcon } from '@angular/material/icon'
import { SelectionComponent } from '../users-selection/selection.component'
import { User, UsersService } from '../users.service'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { setSeconds } from 'date-fns'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { callResourceTypesToUpload } from '../call-document-upload/call-document-upload.component'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { constructNameForUser } from '../utils'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { getMinuteStylesOfOrganization } from '../organizations.service'
import { MatOption } from '@angular/material/core'
import { MatSelect } from '@angular/material/select'

export const MAXIMUM_CALL_DURATION_IN_MINUTES = 540
export const MINIMUM_CALL_DURATION_IN_MINUTES = 30
export const CALL_DURATION_INCREMENT_IN_MINUTES = 30

@Component({
  selector: 'app-add-call',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    AsyncPipe,
    SpinnerComponent,
    MatIcon,
    MatIconButton,
    SelectionComponent,
    MinutesStylingComponent,
    ToolbarComponent,
    MatDatepickerModule,
    MatIconButtonSizesModule,
    FileUploadComponent,
    DatetimePickerComponent,
    MatSelect,
    MatOption,
  ],
  templateUrl: './add-call-page.component.html',
  styleUrl: './add-call-page.component.scss',
})
export class AddCallPageComponent implements OnInit {
  callsService = inject(CallsService)
  addCallMutation = this.callsService.joinLiveCall()
  toastService = inject(ToastService)
  router = inject(Router)
  route = inject(ActivatedRoute)
  http = inject(HttpClient)
  usersService = inject(UsersService)
  currentUserQuery$ = this.usersService.getCurrentUser()

  currentUser$ = this.usersService.currentUser$.pipe(
    take(1),
    tap((user) => {
      if (user === null || this.data !== null) {
        return
      }
      this.data = {
        link: '',
        name: '',
        attendees: [],
        userIds: [],
        ...getMinuteStylesOfOrganization(user.organization),
        date: setSeconds(new Date(), 0),
        duration: 0,
        customDocumentUploadInfo: null,
        agendaUploadInfo: null,
        agendaType: null,
      }
    })
  )

  data: AddCallData | null = null

  uploading = false
  organizationUsers$ = this.usersService.getOrganizationUsers()

  organizationUsersWithoutCurrentUser$ = combineLatest([this.currentUserQuery$, this.organizationUsers$]).pipe(
    map(([currentUserResult, organizationUsersResult]) => {
      if (currentUserResult.isSuccess && organizationUsersResult.isSuccess) {
        const currentUser = currentUserResult.data
        const filteredUsers = organizationUsersResult.data!.filter((user) => user.id !== currentUser!.id)
        return { isSuccess: true, data: filteredUsers }
      } else {
        return { isSuccess: false, data: [] }
      }
    })
  )

  time: string | null = null

  get agendaExtensions(): string[] {
    return callResourceTypesToUpload['AGENDA'].extensions
  }

  get agendaMaxSize(): number | null {
    return callResourceTypesToUpload['AGENDA'].maxSize
  }

  ngOnInit() {
    this.currentUser$.subscribe()

    this.addCallMutation.result$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((call) => {
        this.toastService.show('UPLOAD_CALL.IMPORT_SUCCESSFUL_NOTIFICATION')
        this.router.navigate(['/app', 'meetings', call!.id])
      })
  }

  addFullNameToUserList(users: User[]) {
    return users.map((user) => {
      return {
        ...user,
        fullName: constructNameForUser(user),
      }
    })
  }

  addCall() {
    this.addCallMutation.mutate(this.data!)
  }

  onUserSelectionChange(users: User[]) {
    this.data!.userIds = users.map((user) => user.id)
  }

  updateMinutesStyling(minutesStyling: Partial<MinutesStyling>) {
    this.data = { ...this.data!, ...minutesStyling }
  }

  increaseDuration() {
    if (this.data!.duration < MAXIMUM_CALL_DURATION_IN_MINUTES) {
      this.data!.duration += CALL_DURATION_INCREMENT_IN_MINUTES
    }
  }

  decreaseDuration() {
    if (this.data!.duration > MINIMUM_CALL_DURATION_IN_MINUTES) {
      this.data!.duration -= CALL_DURATION_INCREMENT_IN_MINUTES
    }
  }

  isDateTooFarInPast(date: Date) {
    const now = new Date()
    const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000)
    return date < fifteenMinutesAgo
  }

  get increaseDurationButtonDisabled() {
    return this.data === null || this.data.duration >= MAXIMUM_CALL_DURATION_IN_MINUTES
  }

  get decreaseDurationButtonDisabled() {
    return this.data === null || this.data.duration <= MINIMUM_CALL_DURATION_IN_MINUTES
  }
}
